import { useState } from 'react'
import { ListChecks, X } from 'lucide-react'
import CustomFieldInput from '@/components/CustomFieldInput'
import { todayIso } from '@/lib/dealFormat'
import type { Requirement } from '@/lib/stageRequirements'
import type { MoveFills } from '@/api/deals'
import type { Deal, Stage } from '@/api/types'

const fieldCls =
  'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-primary/40'

function isFilled(v: unknown): boolean {
  return !(v == null || v === '' || (Array.isArray(v) && v.length === 0))
}

/**
 * "Before you move it": the target stage requires fields this deal doesn't have yet (set per stage in
 * Settings → Pipeline). Asks only for those; Move saves them together with the move, Cancel leaves the
 * deal where it was.
 */
export default function StageRequirementsDialog({
  deal,
  stage,
  missing,
  pending,
  error,
  onMove,
  onClose,
}: {
  deal: Deal
  stage: Stage
  missing: Requirement[]
  pending: boolean
  error: string | null
  onMove: (fills: MoveFills) => void
  onClose: () => void
}) {
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [stepTitle, setStepTitle] = useState('Follow up')
  const set = (key: string, v: unknown) => setValues((prev) => ({ ...prev, [key]: v }))
  const pastDate = typeof values.nextContactDate === 'string' && values.nextContactDate < todayIso()
  const complete = missing.every((r) => isFilled(values[r.key])) && !pastDate

  function submit() {
    if (!complete) return
    const fills: MoveFills = {}
    const custom: Record<string, unknown> = {}
    for (const r of missing) {
      if (r.key === 'nextContactDate') {
        fills.nextContactDate = values[r.key] as string
        fills.nextStepTitle = stepTitle.trim() || 'Follow up'
      }
      else if (r.key === 'value') fills.value = values[r.key] as number
      else custom[r.key.slice('custom.'.length)] = values[r.key]
    }
    if (Object.keys(custom).length > 0) fills.custom = custom
    onMove(fills)
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="stage-req-title"
        className="relative bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <ListChecks size={16} className="text-brand-primary" />
            <h2 id="stage-req-title" className="text-sm font-semibold text-brand-darkest">
              Before moving to {stage.name}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" title="Cancel">
            <X size={18} />
          </button>
        </div>
        <form
          className="p-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault()
            submit()
          }}
        >
          <p className="text-xs text-gray-500">
            “{deal.title}” needs {missing.length === 1 ? 'this field' : 'these fields'} filled in for {stage.name}.
          </p>
          {missing.map((r, i) => (
            <div key={r.key}>
              <label className="block text-xs font-medium text-gray-500 mb-1">{r.label}</label>
              {r.key === 'nextContactDate' ? (
                // the next step is a task: what to do, and when (its date is the next contact)
                <div className="flex gap-2">
                  <input
                    aria-label="What to do"
                    autoFocus={i === 0}
                    value={stepTitle}
                    onChange={(e) => setStepTitle(e.target.value)}
                    className={`${fieldCls} flex-1 min-w-0`}
                  />
                  <input
                    type="date"
                    aria-label="When"
                    min={todayIso()}
                    value={(values[r.key] as string) ?? ''}
                    onChange={(e) => set(r.key, e.target.value || null)}
                    className={`${fieldCls} w-40 shrink-0`}
                  />
                </div>
              ) : r.key === 'value' ? (
                <input
                  type="number"
                  min={0}
                  autoFocus={i === 0}
                  value={values[r.key] == null ? '' : String(values[r.key])}
                  onChange={(e) => set(r.key, e.target.value === '' ? null : Number(e.target.value))}
                  className={fieldCls}
                />
              ) : r.def ? (
                <CustomFieldInput def={r.def} value={values[r.key]} onChange={(v) => set(r.key, v)} />
              ) : null}
              {r.key === 'nextContactDate' && pastDate && (
                <p className="text-danger text-xs mt-1">The next step can't be in the past</p>
              )}
            </div>
          ))}
          {error && <p className="text-danger text-xs">{error}</p>}
          <div className="pt-1 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-brand-dark"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!complete || pending}
              className="px-4 py-2 bg-brand-primary text-white rounded-lg text-sm font-medium hover:bg-brand-dark disabled:opacity-60"
            >
              {pending ? 'Moving…' : 'Move'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
